import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useHistory} from "react-router-dom";
import {useOktaAuth} from "@okta/okta-react";
import {Avatar, Col, Image, Layout, Menu, Row, Typography} from "antd";
import Logo from "../assets/LiftoffLogo.png";
import {PATHS} from "./Constants";
import {useUser} from "../auth/userHooks";
import {LiftoffMappingGroups} from './liftoff-mapping/mappings';

const Header = () => {
  const {authState} = useOktaAuth() || {};
  const user = useUser();
  const history = useHistory();
  const [activeKeys, setActiveKeys] = useState("employees");

  const menu_data = useMemo(() => {
    if (user === null) {
      return [];
    } else {
      return [
        {
          key: 'employee_management', path: PATHS.EMPLOYEE_MANAGEMENT, label: 'Employee Management',
          permissions: user.permissions.canReadEmployees || user.permissions.canManageEmployees ||
            user.permissions.canViewScheduled || user.permissions.canManageScheduled,
          children: [
            {
              key: 'employees', path: PATHS.EMPLOYEES, label: 'Employees',
              permissions: user.permissions.canReadEmployees || user.permissions.canManageEmployees,
              children: null
            },
            {
              key: 'bulkUpload', path: PATHS.GET_EMPLOYEES_BULK, label: 'Bulk Upload',
              permissions: user.permissions.canReadEmployees || user.permissions.canManageEmployees,
              children: null
            },
            {
              key: 'scheduled', path: PATHS.SCHEDULED, label: 'Effective Dating',
              permissions: user.permissions.canViewScheduled || user.permissions.canManageScheduled,
              children: null
            }
          ]
        }, {
          key: 'projector', path: '/projector', label: 'Projector',
          permissions: user.permissions.canReadPto || user.permissions.canManagePto || user.permissions.canReadLoa || user.permissions.canManageLoa,
          children: [
            // {
            //   key: 'loa', path: '/projector/loa', label: 'Leave of Absence',
            //   permissions: user.permissions.canReadLoa || user.permissions.canManageLoa,
            //   children: null
            // },
            {
              key: 'pto', path: '/projector/pto', label: 'Time Off',
              permissions: user.permissions.canReadPto || user.permissions.canManagePto,
              children: null
            }
          ]
        }, {
          key: 'd365', path: PATHS.D365, label: 'D365',
          permissions: user.permissions.canViewD365 || user.permissions.canManageD365 ||
            user.permissions.canViewExpenses || user.permissions.canManageExpenses ||
            user.permissions.canViewHours || user.permissions.canManageHours ||
            user.permissions.canViewRates || user.permissions.canManageRates ||
            user.permissions.canViewWorkers || user.permissions.canManageWorkers,
          children: [
            {
              key: 'exports', path: PATHS.D365_EXPORTS, label: 'Exports',
              permissions: user.permissions.canViewD365 || user.permissions.canManageD365,
              children: null
            },
            // {
            //   key: 'adjustments', path: PATHS.D365_ADJUSTMENTS, label: 'Adjustments',
            //   permissions: user.permissions.canViewD365 || user.permissions.canManageD365,
            //   children: [
            //     {
            //       key: 'adjustments_uploads', path: PATHS.D365_ADJUSTMENTS_UPLOADS, label: 'Uploads',
            //       permissions: user.permissions.canViewD365 || user.permissions.canManageD365,
            //       children: null
            //     },
            //     {
            //       key: 'adjustments_all', path: PATHS.D365_ADJUSTMENTS_ALL, label: 'All Records',
            //       permissions: user.permissions.canViewD365 || user.permissions.canManageD365,
            //       children: null
            //     }
            //   ]
            // },
            {
              key: 'expenses', path: PATHS.D365_EXPENSES, label: 'Expenses',
              permissions: user.permissions.canViewExpenses || user.permissions.canManageExpenses,
              children: [
                {
                  key: "unfoundProjects", path: PATHS.D365_EXPENSES_UNFOUND_PROJECTS, label: "Unfound Projects",
                  permissions: user.permissions.canViewExpenses || user.permissions.canManageExpenses,
                  children: null
                },
                {
                  key: "expensesProcessed", path: PATHS.D365_EXPENSES_PROCESSED, label: "Processed",
                  permissions: user.permissions.canViewExpenses || user.permissions.canManageExpenses,
                  children: null
                },
                {
                  key: "expenseTypes",
                  path: PATHS.D365_EXPENSES_PROJECTOR_EXPENSE_TYPES,
                  label: "Projector Expense Types",
                  permissions: user.permissions.canViewExpenses || user.permissions.canManageExpenses,
                  children: null
                },
                {
                  key: "expenseCodes", path: PATHS.D365_EXPENSES_CODES, label: "Codes",
                  permissions: user.permissions.canViewExpenses || user.permissions.canManageExpenses,
                  children: null
                },
                {
                  key: "expenseMappings", path: PATHS.D365_EXPENSES_MAPPINGS, label: "Mappings",
                  permissions: user.permissions.canViewExpenses || user.permissions.canManageExpenses,
                  children: null
                },
                {
                  key: "expenseFiles", path: PATHS.D365_EXPENSES_FILES, label: "Files",
                  permissions: user.permissions.canManageExpenses,
                  children: null
                }
              ]
            }, {
              key: "payments", path: PATHS.D365_PAYMENTS, label: "Payments",
              permissions: user.permissions.canViewD365 || user.permissions.canManageD365,
              children: null
            }, {
              key: "hours", path: PATHS.D365_HOURS, label: "Hours",
              permissions: user.permissions.canViewHours || user.permissions.canManageHours,
              children: null
            }, {
              key: "rates", path: PATHS.D365_RATES, label: "Rates",
              permissions: user.permissions.canViewRates || user.permissions.canManageRates,
              children: null
            }, {
              key: "workers", path: PATHS.D365_WORKERS, label: "Workers",
              permissions: user.permissions.canViewWorkers || user.permissions.canManageWorker,
              children: null
            }, {
              key: "hourLogPeriods", path: PATHS.D365_HOUR_LOG_PERIODS, label: "Hour Log Periods",
              permissions: user.permissions.canViewHourLogPeriods,
              children: null
            }, {
              key: "projectSearch", path: PATHS.D365_PROJECT_SEARCH, label: "Project Search",
              permissions: user.permissions.canManageD365,
              children: null
            }
          ]
        },
        {
          key: 'liftoffMapping',
          path: PATHS.LIFTOFF_MAPPING,
          label: 'Liftoff Mapping',
          permissions: user.permissions.canManageLiftoff,
          children: LiftoffMappingGroups.map(group => {
            return {
              key: `liftoff_mapping_${group.group}`,
              path: `${PATHS.LIFTOFF_MAPPING}/${group.group}`,
              label: group.title,
              permissions: user.permissions.canManageLiftoff,
              children: group.mappings.map(mapping => {
                if (mapping.type === 'divider') {
                  return {
                    type: 'divider'
                  };
                }
                return {
                  key: mapping.entity,
                  path: `${PATHS.LIFTOFF_MAPPING}/${group.group}/${mapping.entity}`,
                  label: mapping.title,
                  permissions: user.permissions.canManageLiftoff,
                  children: null
                }
              })
            }
          })
        },
        {
          key: 'liftoffAdmin',
          path: PATHS.LIFTOFF,
          label: 'Liftoff Admin',
          permissions: user.permissions.canManageLiftoff,
          children: [
            {
              key: 'liftoffUsers',
              path: PATHS.LIFTOFF_USERS,
              label: 'Liftoff Users',
              permissions: user.permissions.canManageLiftoff,
              children: null
            }
          ]
        }
      ];
    }
  }, [user]);


  const findMenuObjectWithPath = useCallback((path, data) => {
    for (let i = 0; i < data.length; i++) {
      if (data[i].type === 'divider') {
        continue;
      }
      if (data[i].path === path) {
        return data[i];
      }
      if (data[i].children !== null) {
        let find = findMenuObjectWithPath(path, data[i].children);
        if (find !== null) {
          return find;
        }
      }
    }
    return null;
  }, []);

  const findMenuObjectWithKey = (key, data) => {
    for (let i = 0; i < data.length; i++) {
      if (data[i].type === 'divider') {
        continue;
      }
      if (data[i].key === key) {
        return data[i];
      }
      if (data[i].children !== null) {
        let find = findMenuObjectWithKey(key, data[i].children);
        if (find !== null) {
          return find;
        }
      }
    }
    return null;
  };

  useEffect(() => {
    if (user !== null) {
      const path = window.location.pathname;
      if (Object.values(PATHS).indexOf(path) > -1) {
        const menu = findMenuObjectWithPath(path, menu_data);
        if (menu !== null && menu.permissions) {
          setActiveKeys(menu.key);
          return;
        }
      }
      for (let i = 0; i < menu_data.length; i++) {
        if (menu_data[i].children !== null) {
          for (let j = 0; j < menu_data[i].children.length; j++) {
            if (menu_data[i].children[j].permissions) {
              setActiveKeys(menu_data[i].children[j].key);
              history.push(menu_data[i].children[j].path);
              return;
            }
          }
        }
      }
    }
  }, [user, history, menu_data, findMenuObjectWithPath]);

  const onChange = (e) => {
    const menu = findMenuObjectWithKey(e.key, menu_data);
    if (menu === null) {
      history.push(PATHS.NOPERMISSION);
    } else {
      setActiveKeys(e.key);
      history.push(menu.path);
    }
  };

  const createMenu = (data) => {
    let menu = [];
    for (let i = 0; i < data.length; i++) {
      if (data[i].type === 'divider') {
        menu.push({type: 'divider'});
        continue;
      }
      let obj = {
        key: data[i].key,
        label: data[i].label,
        path: data[i].path
      };
      if (data[i].permissions) {
        obj.style = {display: 'block'};
      } else {
        obj.style = {display: 'none'};
      }
      if (data[i].children === null) {
        obj.children = null;
      } else {
        obj.children = createMenu(data[i].children);
      }
      menu.push(obj);
    }
    return menu;
  };

  const menu = createMenu(menu_data);
  menu.push({
    style: {cursor: 'auto'},
    label: "", key: "glitch", disabled: true
  });

  return (<Layout.Header style={{backgroundColor: "#DDD"}}>
    <Row>
      <Col span={1}>
        <Avatar src={<Image src={Logo} preview={false}/>} size={50}/>
      </Col>
      <Col>
        <Typography.Text style={{fontSize: 32}}>Liftoff</Typography.Text>
      </Col>
      <Col span={1}></Col>
      <Col flex="auto">
        <Menu style={{backgroundColor: "#DDD", borderBottom: "0px"}} defaultSelectedKeys={["employees"]}
              selectedKeys={[activeKeys]}
              onClick={onChange}
              mode="horizontal" items={menu}/>
      </Col>
      <Col flex="auto"></Col>
      <Col>
        {authState && authState.isAuthenticated && user && (<>
          <Typography.Text style={{marginRight: 8}}>
            You are logged in as{" "}
            <b>
              {user.firstName} {user.lastName} ({user.email})
            </b>
          </Typography.Text>
        </>)}
      </Col>
    </Row>
  </Layout.Header>);
};

export {Header};
